import { useEffect, useRef } from 'react';

import EventBus from 'lib/EventBus';
import { useShallow } from 'zustand/shallow';

import useSelectedDateRange from 'components/roster-portal/activity-planning/hooks/useSelectedDateRange';
import { getMatchingEntityIdentifiers } from 'components/mrp/mrp.utils';
import { useEntityStore } from 'components/mrp/stores/entityStore/entityStore';
import { PlanningEntitiesWebsocketBody } from 'RestAPIs/activities/types';
import { isEqualToSessionCorrelationToken } from 'components/roster-portal/activity-planning/utils';

const EVENT_BUS_URL = '/activity-planning';

export const useMrpWebSocket = () => {
  const { startDate, endDate } = useSelectedDateRange();
  const { subscribers, fetch } = useEntityStore(
    useShallow((state) => ({
      subscribers: state.subscribers,
      fetch: state.control.fetch
    }))
  );

  const stateRef = useRef({ subscribers, startDate, endDate, fetch });
  stateRef.current = { subscribers, startDate, endDate, fetch };

  useEffect(() => {
    if (subscribers.length === 0) return;

    const onPlanningUpdate = async (update: { type: string; body: string }) => {
      if (update.type !== 'planningEntitiesUpdated') return;
      try {
        const {
          planningEntityIdentifiers,
          correlationToken
        }: PlanningEntitiesWebsocketBody = JSON.parse(update.body);

        if (
          !correlationToken ||
          isEqualToSessionCorrelationToken(correlationToken)
        ) {
          return;
        }

        const { subscribers, startDate, endDate, fetch } = stateRef.current;
        const matchingIdentifiers = getMatchingEntityIdentifiers(
          subscribers,
          planningEntityIdentifiers
        );
        if (matchingIdentifiers.length === 0) return;
        await fetch({
          planningEntityIdentifiers: matchingIdentifiers,
          startDate,
          endDate
        });
      } catch {
        // WebSocket messages are non-critical; swallow parse/fetch errors
      }
    };

    EventBus.register(EVENT_BUS_URL, onPlanningUpdate);

    return () => {
      EventBus.unregister(EVENT_BUS_URL, onPlanningUpdate);
    };
  }, [subscribers.length]);
};
